import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import AdminService from '../../../services/adminService';
import CountryForm from '../../../components/admin/CountryForm';
import CountryCard from '../../../components/admin/CountryCard';


// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Country Manager Component
 *
 * Provides comprehensive CRUD operations for countries:
 * - Add new countries with flag emojis
 * - Edit existing country details
 * - Delete countries (with confirmation)
 * - View country statistics and pathways
 * - Manage country order for display
 */
const CountryManager = () => {
  const { t } = useLanguage();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = useCallback(async () => {
    try {
      setLoading(true);
      console.log('[CountryManager] Loading countries...');
      const data = await AdminService.getCountriesThrottled();
      setCountries(data || []);
      setError('');
      console.log('[CountryManager] Countries loaded:', data?.length || 0);
    } catch (err) {
      console.error('Error loading countries:', err);
      setError(t('admin.countries.error.load', 'Failed to load countries'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCreate = () => {
    setEditingCountry(null);
    setShowForm(true);
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setShowForm(true);
  };

  const handleDelete = async (countryId) => {
    if (!window.confirm(t('admin.countries.confirm_delete', 'Are you sure you want to delete this country?'))) {
      return;
    }

    try {
      await AdminService.deleteCountry(countryId);
      await loadCountries();
    } catch (err) {
      console.error('Error deleting country:', err);
      setError(t('admin.countries.error.delete', 'Failed to delete country'));
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingCountry) {
        await AdminService.updateCountry(editingCountry.id, formData);
      } else {
        await AdminService.createCountry(formData);
      }
      setShowForm(false);
      setEditingCountry(null);
      await loadCountries();
    } catch (err) {
      console.error('Error saving country:', err);
      setError(t('admin.countries.error.save', 'Failed to save country'));
    }
  };

  const handleReorder = async (countryId, newOrder) => {
    try {
      await AdminService.updateCountry(countryId, { sort_order: newOrder });
      await loadCountries();
    } catch (err) {
      console.error('Error reordering country:', err);
      setError(t('admin.countries.error.reorder', 'Failed to reorder countries'));
    }
  };

  const moveCountryUp = async (index) => {
    if (index > 0) {
      const country = countries[index];
      const previousCountry = countries[index - 1];

      await Promise.all([
        handleReorder(country.id, country.sort_order - 1),
        handleReorder(previousCountry.id, previousCountry.sort_order + 1)
      ]);
    }
  };

  const moveCountryDown = async (index) => {
    if (index < countries.length - 1) {
      const country = countries[index];
      const nextCountry = countries[index + 1];

      await Promise.all([
        handleReorder(country.id, country.sort_order + 1),
        handleReorder(nextCountry.id, nextCountry.sort_order - 1)
      ]);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p className="mt-2 text-gray-400">Loading countries...</p>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      {/* Header */}
      <div className="admin-header">
        <h2 className="admin-title">
          🌍 Countries Management
        </h2>
        <p className="admin-subtitle">
          Manage destination countries and their properties
        </p>
        <div className="admin-actions mt-4">
          <button onClick={handleCreate} className="admin-btn admin-btn-primary admin-btn-sm">
            ➕ {t('admin.countries.add_new', 'Add New Country')}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="admin-alert admin-alert-error">
          <p>{error}</p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="admin-modal">
          <div className="admin-modal-content bg-gray-800 text-white border border-gray-700">
            <CountryForm
              country={editingCountry}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingCountry(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Countries List */}
      <div className="admin-card bg-gray-800 border-gray-700">
        <div className="admin-card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">
              {t('admin.countries.list_title', 'All Countries')}
            </h3>
            <span className="text-sm font-medium text-gray-600">
              {countries.length} {t('admin.countries.total', 'total')}
            </span>
          </div>
        </div>

        {countries.length === 0 ? (
          <div className="admin-card-content text-center">
            <div className="text-6xl mb-4">🌍</div>
            <h3 className="text-lg font-medium text-white mb-2">
              {t('admin.countries.no_countries', 'No countries yet')}
            </h3>
            <p className="text-gray-400 mb-4">
              {t('admin.countries.add_first', 'Add your first destination country to get started')}
            </p>
            <button onClick={handleCreate} className="admin-btn admin-btn-primary admin-btn-sm">
              ➕ {t('admin.countries.add_first_button', 'Add First Country')}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {countries.map((country, index) => (
              <div key={country.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Reorder Controls */}
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => moveCountryUp(index)}
                        disabled={index === 0}
                        className="p-1 text-gray-500 hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveCountryDown(index)}
                        disabled={index === countries.length - 1}
                        className="p-1 text-gray-500 hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Country Flag */}
                    <div className="text-4xl">
                      {country.flag_emoji}
                    </div>

                    {/* Country Info */}
                    <div className="flex-1">
                      <h4 className="text-xl font-semibold text-white mb-2">{country.name}</h4>
                      <div className="flex items-center gap-3">
                        <span className={`admin-badge ${country.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                          {country.is_active ? '✓ Active' : '✕ Inactive'}
                        </span>
                        <span className="text-sm text-gray-400 bg-gray-700 px-2 py-1 rounded">
                          Code: {country.code}
                        </span>
                        <span className="text-sm text-gray-400 bg-gray-700 px-2 py-1 rounded">
                          Order: {country.sort_order}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(country)}
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      title={t('admin.edit', 'Edit')}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(country.id)}
                      className="admin-btn admin-btn-danger admin-btn-sm"
                      title={t('admin.delete', 'Delete')}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CountryManager;